import React from 'react';
import { createPortal } from 'react-dom';
import type { EvaluationPlanAttachmentSlot } from '../../types/evaluationPlan';
import { EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS } from '../../types/evaluationPlan';
import {
  formatEvaluationPlanDocumentDataBytes,
  type EvaluationPlanDocumentListItem,
} from '../../utils/evaluationPlanDocumentList';
import { isShareLinkAttachment, isSheetCanvasAttachment } from '../../utils/evaluationPlanAttachment';
import ModalBodyLoadingSpinner from '../common/ModalBodyLoadingSpinner';
import EvaluationPlanDeleteHoldButton from './EvaluationPlanDeleteHoldButton';
import EvaluationPlanAttachmentPreviewModal from './EvaluationPlanAttachmentPreviewModal';
import EvaluationPlanSheetCanvasViewerModal from './EvaluationPlanSheetCanvasViewerModal';
import EvaluationPlanWebDocumentViewerModal from './EvaluationPlanWebDocumentViewerModal';

function listOpenCellProps(
  row: EvaluationPlanDocumentListItem,
  onOpen: () => void,
  extraClass = ''
): React.TdHTMLAttributes<HTMLTableCellElement> {
  if (isShareLinkAttachment(row.attachment)) {
    return {
      className: `cp-program-presentation-list-open-cell cp-program-presentation-list-open-cell-disabled${extraClass ? ` ${extraClass}` : ''}`,
      title: '공유 링크 문서는 미리보기 대신 평가계획에서 열어 주세요.',
      'aria-disabled': true,
    };
  }
  return {
    className: `cp-program-presentation-list-open-cell${extraClass ? ` ${extraClass}` : ''}`,
    tabIndex: 0,
    role: 'button',
    onClick: onOpen,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

export type EvaluationPlanDocumentListModalProps = {
  open: boolean;
  onClose: () => void;
  slot: EvaluationPlanAttachmentSlot;
  items: EvaluationPlanDocumentListItem[];
  isLoading?: boolean;
  loadError?: string | null;
  actionsDisabled?: boolean;
  title?: string;
  onDeleteItem: (listKey: string) => Promise<boolean>;
  onNotify?: (message: string) => void;
};

const EvaluationPlanDocumentListModal: React.FC<EvaluationPlanDocumentListModalProps> = ({
  open,
  onClose,
  slot,
  items,
  isLoading = false,
  loadError = null,
  actionsDisabled = false,
  title: titleOverride,
  onDeleteItem,
  onNotify,
}) => {
  const [previewTarget, setPreviewTarget] = React.useState<EvaluationPlanDocumentListItem | null>(null);
  const [webDocumentViewerTarget, setWebDocumentViewerTarget] = React.useState<EvaluationPlanDocumentListItem | null>(
    null
  );
  const [sheetCanvasViewerTarget, setSheetCanvasViewerTarget] = React.useState<EvaluationPlanDocumentListItem | null>(
    null
  );
  const [deleteBusyKey, setDeleteBusyKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setPreviewTarget(null);
      setWebDocumentViewerTarget(null);
      setSheetCanvasViewerTarget(null);
      setDeleteBusyKey(null);
    }
  }, [open]);

  const slotLabel = EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS[slot];
  const title = titleOverride ?? (slot === 'criterion' ? '기준안 문서 목록' : '평가지 문서 목록');

  const openItem = (item: EvaluationPlanDocumentListItem) => {
    if (isShareLinkAttachment(item.attachment)) return;
    if (item.attachment.kind === 'file' || item.attachment.kind === 'form') {
      setPreviewTarget(item);
      return;
    }
    if (slot === 'sheet' && isSheetCanvasAttachment(item.attachment)) {
      setSheetCanvasViewerTarget(item);
      return;
    }
    setWebDocumentViewerTarget(item);
  };

  const handleDelete = async (listKey: string) => {
    if (deleteBusyKey) return;
    setDeleteBusyKey(listKey);
    try {
      const ok = await onDeleteItem(listKey);
      if (ok) {
        onNotify?.(`${slotLabel} 문서가 삭제되었습니다.`);
      } else {
        onNotify?.('삭제할 문서를 찾지 못했습니다.');
      }
    } catch (error) {
      onNotify?.(error instanceof Error ? error.message : '문서 삭제에 실패했습니다.');
    } finally {
      setDeleteBusyKey(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="cp-modal-backdrop cp-evaluation-plan-document-list-backdrop"
        role="presentation"
        onMouseDown={(ev) => {
          if (ev.target === ev.currentTarget) onClose();
        }}
      >
        <div
          className="cp-modal cp-program-presentation-list-modal cp-evaluation-plan-document-list-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-evaluation-plan-document-list-title"
          onMouseDown={(ev) => ev.stopPropagation()}
        >
          <h3 id="cp-evaluation-plan-document-list-title">{title}</h3>
          <p className="page-desc cp-program-presentation-list-intro">
            등록된 {slotLabel} 웹 문서입니다. 행을 클릭하면 미리보기가 열립니다(모달은 유지됩니다).{' '}
            <strong>내용</strong> 열이 <span className="cp-evaluation-plan-document-list-empty-mark">비어 있음</span>인
            항목은 불필요한 문서인지 확인해 보세요. 삭제는 휴지통을 1초간 길게 누릅니다.
          </p>
          {loadError ? (
            <p className="cp-program-data-cleanup-error" role="alert">
              {loadError}
            </p>
          ) : null}
          <div className="cp-modal-body cp-program-presentation-list-body" aria-busy={isLoading || undefined}>
            {isLoading ? (
              <ModalBodyLoadingSpinner label={`${slotLabel} 문서 목록 불러오는 중`} />
            ) : items.length ? (
              <div className="cp-program-presentation-list-table-wrap">
                <table className="cp-program-presentation-list-table cp-evaluation-plan-document-list-table">
                  <thead>
                    <tr>
                      <th scope="col">카테고리</th>
                      <th scope="col">평가계획</th>
                      <th scope="col">연결 성취기준</th>
                      <th scope="col">종류</th>
                      <th scope="col">내용</th>
                      <th scope="col">등록</th>
                      <th scope="col">용량</th>
                      <th scope="col">작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const openRowHandler = () => openItem(row);
                      const rowClassName = [
                        'cp-program-presentation-list-row',
                        row.isEmptyContent ? 'cp-evaluation-plan-document-list-row-empty' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <tr key={row.listKey} className={rowClassName}>
                          <td {...listOpenCellProps(row, openRowHandler)}>{row.categoryName}</td>
                          <td {...listOpenCellProps(row, openRowHandler)} title={row.planSummary}>
                            {row.planSummary}
                          </td>
                          <td {...listOpenCellProps(row, openRowHandler)} title={row.linkedStandardsSummary}>
                            {row.linkedStandardsSummary}
                          </td>
                          <td {...listOpenCellProps(row, openRowHandler)}>{row.kindLabel}</td>
                          <td
                            {...listOpenCellProps(row, openRowHandler)}
                            className={
                              row.isEmptyContent
                                ? 'cp-evaluation-plan-document-list-empty-mark cp-program-presentation-list-open-cell'
                                : 'cp-program-presentation-list-open-cell'
                            }
                          >
                            {row.contentLabel}
                          </td>
                          <td {...listOpenCellProps(row, openRowHandler)}>{row.registeredAtLabel}</td>
                          <td
                            {...listOpenCellProps(row, openRowHandler, 'cp-program-presentation-list-col-num')}
                          >
                            {formatEvaluationPlanDocumentDataBytes(row.dataBytes)}
                          </td>
                          <td className="cp-program-presentation-list-actions-cell">
                            <EvaluationPlanDeleteHoldButton
                              itemId={row.listKey}
                              disabled={actionsDisabled || deleteBusyKey === row.listKey}
                              className="cp-evaluation-plan-document-list-delete-btn"
                              onRemove={() => void handleDelete(row.listKey)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="page-desc">등록된 {slotLabel} 문서가 없습니다.</p>
            )}
          </div>
          <div className="cp-modal-footer cp-program-presentation-list-footer">
            <button type="button" className="cp-btn-secondary" onClick={() => onClose()}>
              닫기
            </button>
          </div>
        </div>
      </div>

      <EvaluationPlanAttachmentPreviewModal
        open={previewTarget != null}
        slot={slot}
        attachment={previewTarget?.attachment ?? null}
        onClose={() => setPreviewTarget(null)}
        stackAboveDocumentList
      />

      <EvaluationPlanWebDocumentViewerModal
        open={webDocumentViewerTarget != null}
        slot={slot}
        attachment={webDocumentViewerTarget?.attachment ?? null}
        onClose={() => setWebDocumentViewerTarget(null)}
        stackAboveDocumentList
      />

      <EvaluationPlanSheetCanvasViewerModal
        open={sheetCanvasViewerTarget != null}
        slot="sheet"
        attachment={sheetCanvasViewerTarget?.attachment ?? null}
        onClose={() => setSheetCanvasViewerTarget(null)}
        stackAboveDocumentList
      />
    </>,
    document.body
  );
};

export default EvaluationPlanDocumentListModal;
